import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { logger } from "#app/monitoring/Logger";
import { showErrorToast } from "#app/notifications";
import { Toggle } from "#components/atoms/Toggle/Toggle";
import { SelectableCard } from "#components/molecules/SelectableCard/SelectableCard";
import { Company } from "#modules/company/infra/Company.dto";
import { useCompany } from "#modules/company/view/useCompany";
import { CreateDefaultProcessStep } from "#modules/defaultProcessSteps/infra/createDefaultProcessStep.api";
import { useCreateDefaultProcessStep } from "#modules/defaultProcessSteps/view/useCreateDefaultProcessStep";
import { useGetDefaultProcessStep } from "#modules/defaultProcessSteps/view/useGetDefaultProcessStep";
import { useUpdateDefaultProcessStep } from "#modules/defaultProcessSteps/view/useUpdateDefaultProcessStep";
import { predictedProcessStepsAdapter } from "#modules/quoteRequestLines/adapters/predictedProcessStepsAdapter";
import { useProcessStepCatalog } from "#modules/quoteRequestLines/view/useProcessStepCatalog";
import { filterInHouseProcessSteps } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/ProcessSteps";
import {
  buildProcessStepOptionValue,
  PREFIX,
  useProcessStepsSelectData,
} from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/useProcessStepsSelectData";
import { isPlainNumberString } from "#shared/helpers/isPlainNumberString";
import { centsToEuro, euroToCents } from "#shared/helpers/prices";
import { Group } from "#shared/view/components/Group";
import { Label } from "#shared/view/components/Label";
import { Modal } from "#shared/view/components/Modal";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { Button } from "#shared/view/designSystem/Button";
import inputClasses from "#shared/view/designSystem/inputs/InputStyles.module.css";
import { NumberInput } from "#shared/view/designSystem/inputs/NumberInput";
import { TextInput } from "#shared/view/designSystem/inputs/TextInput";
import { SelectInput } from "#shared/view/designSystem/SelectInput";
import {
  IndustrialProcessSchema,
  IndustrialProcessType,
  MaterialGradeSchema,
  PartLevel,
} from "shared";

import { DefaultProcessStepForTable } from "./DefaultProcessStepsTable";

type Props =
  | {
      opened: boolean;
      onClose: () => void;
      mode: "create";
      initialData?: undefined;
      onSuccess: (created: CreateDefaultProcessStep) => void;
      index: number;
      companyId: string;
    }
  | {
      opened: boolean;
      onClose: () => void;
      mode: "edit";
      initialData: DefaultProcessStepForTable;
      onSuccess: (updated: CreateDefaultProcessStep) => void;
      index?: undefined;
      companyId: string;
    };

type TimeMode = "model" | "fixed";

const getTimeMode = (value: string | null): TimeMode => (value === null ? "model" : "fixed");

type PartLevelsPreset = "PARENT_OR_SINGLE" | "CHILD_ONLY" | "ALL";

export const PART_LEVELS_PRESETS: Record<PartLevelsPreset, PartLevel[]> = {
  PARENT_OR_SINGLE: ["PARENT", "ORPHELIN"],
  CHILD_ONLY: ["CHILD"],
  ALL: ["PARENT", "ORPHELIN", "CHILD"],
};

const PART_LEVELS_PRESET_KEYS: PartLevelsPreset[] = ["PARENT_OR_SINGLE", "CHILD_ONLY", "ALL"];

const arePartLevelsEqual = (a: PartLevel[], b: PartLevel[]) =>
  a.length === b.length && a.every((level) => b.includes(level));

export const getPartLevelsPreset = (
  partLevels: PartLevel[],
  companyId: string,
): PartLevelsPreset => {
  const matchingPreset = PART_LEVELS_PRESET_KEYS.find((preset) =>
    arePartLevelsEqual(partLevels, PART_LEVELS_PRESETS[preset]),
  );

  if (matchingPreset) {
    return matchingPreset;
  }
  logger.error(
    `No matching preset found for part levels ${partLevels.join(", ")} and company ${companyId}`,
  );

  return "ALL";
};

export const canHaveAssemblies = (company: Company | null | undefined) =>
  company?.companySettings.assemblySplitMode !== "LEAF";

const getFormState = (initialData?: DefaultProcessStepForTable) => ({
  industrialProcess: initialData?.industrialProcess ?? IndustrialProcessSchema.enum.OTHER,
  description: initialData?.description ?? null,
  preparationTimeFormula: initialData?.preparationTimeFormula ?? null,
  cycleTimeFormula: initialData?.cycleTimeFormula ?? null,
  machineHourlyPrice: initialData?.machineHourlyPrice
    ? centsToEuro(initialData.machineHourlyPrice)
    : 0,
  partLevels: initialData?.partLevels ?? PART_LEVELS_PRESETS.ALL,
});

export const DefaultProcessStepFormModal = ({
  opened,
  onClose,
  mode,
  initialData,
  onSuccess,
  index,
  companyId,
}: Props) => {
  const { t } = useTranslation();

  const [industrialProcess, setIndustrialProcess] = useState<IndustrialProcessType>(
    getFormState(initialData).industrialProcess,
  );
  const [description, setDescription] = useState<string | null>(
    getFormState(initialData).description,
  );
  const [preparationTimeFormula, setPreparationTimeFormula] = useState<string | null>(
    getFormState(initialData).preparationTimeFormula,
  );
  const [preparationTimeMode, setPreparationTimeMode] = useState<TimeMode>(
    getTimeMode(getFormState(initialData).preparationTimeFormula),
  );
  const [cycleTimeFormula, setCycleTimeFormula] = useState<string | null>(
    getFormState(initialData).cycleTimeFormula,
  );
  const [otherMachineTimeMode, setOtherMachineTimeMode] = useState<TimeMode>(
    getTimeMode(getFormState(initialData).cycleTimeFormula),
  );
  const [machineHourlyPrice, setMachineHourlyPrice] = useState(
    getFormState(initialData).machineHourlyPrice,
  );
  const [partLevels, setPartLevels] = useState<PartLevel[]>(getFormState(initialData).partLevels);
  const [selectedProcessValue, setSelectedProcessValue] = useState<string | null>(null);
  const [isComputedFromSurfaceTreatment, setIsComputedFromSurfaceTreatment] = useState(false);
  const hasResolvedSelectValue = useRef(false);

  const { company } = useCompany();
  const { processStepCatalog } = useProcessStepCatalog();

  const allPredictedProcessSteps = useMemo(
    () => predictedProcessStepsAdapter(processStepCatalog),
    [processStepCatalog],
  );

  const { allProcessStepsDataForSelect, getProcessStepToAddFromSelect } = useProcessStepsSelectData(
    {
      allPredictedProcessSteps,
      additionalProcessSteps: processStepCatalog,
      filterProcessSteps: filterInHouseProcessSteps,
      materialGrade: MaterialGradeSchema.enum.OTHER,
    },
  );

  useEffect(() => {
    if (!opened) {
      hasResolvedSelectValue.current = false;
      return;
    }

    const formState = getFormState(initialData);
    setIndustrialProcess(formState.industrialProcess);
    setDescription(formState.description);
    setPreparationTimeFormula(formState.preparationTimeFormula);
    setPreparationTimeMode(getTimeMode(formState.preparationTimeFormula));
    setCycleTimeFormula(formState.cycleTimeFormula);
    setOtherMachineTimeMode(getTimeMode(formState.cycleTimeFormula));
    setMachineHourlyPrice(formState.machineHourlyPrice);
    setPartLevels(formState.partLevels);
    setIsComputedFromSurfaceTreatment(false);

    if (!initialData) {
      setSelectedProcessValue(null);
      hasResolvedSelectValue.current = true;
    } else {
      hasResolvedSelectValue.current = false;
    }
  }, [opened, initialData]);

  useEffect(() => {
    if (!opened || !initialData || hasResolvedSelectValue.current) {
      return;
    }

    const optionValues = allProcessStepsDataForSelect.flatMap((group) =>
      group.items.map((item) => item.value),
    );
    if (optionValues.length === 0) {
      return;
    }

    const withPrefix = buildProcessStepOptionValue(
      {
        industrialProcess: initialData.industrialProcess,
        description: initialData.description,
      },
      PREFIX,
    );
    const withoutPrefix = buildProcessStepOptionValue({
      industrialProcess: initialData.industrialProcess,
      description: initialData.description,
    });

    setSelectedProcessValue(
      optionValues.includes(withPrefix)
        ? withPrefix
        : optionValues.includes(withoutPrefix)
          ? withoutPrefix
          : withPrefix,
    );
    hasResolvedSelectValue.current = true;
  }, [opened, initialData, processStepCatalog, allProcessStepsDataForSelect]);

  const { updateDefaultProcessStep, isPending: isUpdating } = useUpdateDefaultProcessStep({
    onSuccess: (updated) => {
      if (mode === "edit") {
        onSuccess(updated);
      }
    },
  });

  const { createDefaultProcessStep, isPending: isCreating } = useCreateDefaultProcessStep({
    onSuccess: (created) => {
      if (mode === "create") {
        onSuccess(created);
      }
    },
  });

  const { getDefaultProcessStep } = useGetDefaultProcessStep();

  const handlePreparationTimeModeChange = (timeMode: TimeMode) => {
    setPreparationTimeMode(timeMode);
    if (timeMode === "fixed" && preparationTimeFormula === null) {
      setPreparationTimeFormula("0");
    }
  };

  const handleOtherMachineTimeModeChange = (timeMode: TimeMode) => {
    setOtherMachineTimeMode(timeMode);
    if (timeMode === "fixed" && cycleTimeFormula === null) {
      setCycleTimeFormula("0");
    }
  };

  const applyPredictedProcessStep = async (
    processIndustrialProcess: IndustrialProcessType,
    processDescription?: string | null,
  ) => {
    const predicted = await getDefaultProcessStep({
      industrialProcess: processIndustrialProcess,
      description: processDescription,
    });

    if (!predicted) {
      return;
    }

    if (predicted.isComputedFromSurfaceTreatment) {
      setIsComputedFromSurfaceTreatment(true);
      setPreparationTimeFormula(null);
      setPreparationTimeMode("model");
      setCycleTimeFormula(null);
      setOtherMachineTimeMode("model");
      setMachineHourlyPrice(0);
      return;
    }

    setMachineHourlyPrice(
      predicted.machineHourlyPrice !== null ? centsToEuro(predicted.machineHourlyPrice) : 0,
    );
    setPreparationTimeFormula(predicted.preparationTimeFormula);
    setPreparationTimeMode(getTimeMode(predicted.preparationTimeFormula));
    setCycleTimeFormula(predicted.cycleTimeFormula);
    setOtherMachineTimeMode(getTimeMode(predicted.cycleTimeFormula));
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    setIsComputedFromSurfaceTreatment(false);
  };

  const handleDescriptionBlur = () => {
    if (!description) {
      return;
    }
    void applyPredictedProcessStep(industrialProcess, description);
  };

  const handleProcessChange = async (value: string | null) => {
    setSelectedProcessValue(value);
    const processStep = getProcessStepToAddFromSelect(value);
    if (!processStep) {
      return;
    }

    setIndustrialProcess(processStep.industrialProcess);
    setDescription(processStep.description ?? null);
    setIsComputedFromSurfaceTreatment(false);

    await applyPredictedProcessStep(processStep.industrialProcess, processStep.description);
  };

  const handleSubmit = () => {
    if (preparationTimeMode === "fixed" && preparationTimeFormula === "") {
      showErrorToast({
        title: t("settings.defaultProcessStepTimeMode.missingFieldError"),
        message: t("settings.defaultProcessStepTimeMode.missingPreparationTime"),
      });
      return;
    }
    if (otherMachineTimeMode === "fixed" && cycleTimeFormula === "") {
      showErrorToast({
        title: t("settings.defaultProcessStepTimeMode.missingFieldError"),
        message: t("settings.defaultProcessStepTimeMode.missingOtherMachineTime"),
      });
      return;
    }

    const payload = {
      industrialProcess,
      description,
      preparationTimeFormula:
        preparationTimeMode === "model" ? null : (preparationTimeFormula ?? "0"),
      cycleTimeFormula: otherMachineTimeMode === "model" ? null : (cycleTimeFormula ?? "0"),
      machineHourlyPrice: isComputedFromSurfaceTreatment ? null : euroToCents(machineHourlyPrice),
      partLevels,
    };

    if (mode === "edit") {
      updateDefaultProcessStep({ id: initialData.id, payload });
    } else {
      createDefaultProcessStep({ payload: { ...payload, index } });
    }
  };

  const title =
    mode === "create" ? t("settings.addDefaultProcessStep") : t("settings.editDefaultProcessStep");
  const isPending = isUpdating || isCreating;
  const selectedPartLevelsPreset = getPartLevelsPreset(partLevels, companyId);
  const displayPartLevels = canHaveAssemblies(company);

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="md">
      <Stack gap="md">
        <SelectInput
          label={t("quoteRequestLine.processSteps")}
          data={allProcessStepsDataForSelect}
          value={selectedProcessValue}
          onChange={handleProcessChange}
          searchable
          placeholder={t("quoteRequestLine.addProcessStepPlaceholder")}
        />

        <TextInput
          label={t("processStep.description")}
          value={description ?? ""}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          onBlur={handleDescriptionBlur}
        />

        {isComputedFromSurfaceTreatment ? (
          <Text size="xs" c="dimmed">
            {t("settings.defaultProcessStepComputedFromSurfaceTreatment")}
          </Text>
        ) : (
          <>
            <Stack gap="xxs">
              <Group justify="space-between" align="center" gap={0}>
                <Label classNames={{ label: inputClasses.label }} helper={undefined}>
                  {t("processStep.machinePreparationTime")}
                </Label>
                <Toggle
                  value={preparationTimeMode}
                  onChange={handlePreparationTimeModeChange}
                  items={[
                    { value: "model", label: t("settings.defaultProcessStepTimeMode.model") },
                    { value: "fixed", label: t("settings.defaultProcessStepTimeMode.fixed") },
                  ]}
                />
              </Group>
              {preparationTimeMode === "fixed" && (
                <TextInput
                  value={preparationTimeFormula ?? ""}
                  onChange={(e) => setPreparationTimeFormula(e.target.value)}
                  rightSection={
                    preparationTimeFormula && isPlainNumberString(preparationTimeFormula)
                      ? "min"
                      : undefined
                  }
                />
              )}
            </Stack>

            <Stack gap="xxs">
              <Group justify="space-between" align="center" gap={0}>
                <Label classNames={{ label: inputClasses.label }} helper={undefined}>
                  {t("processStep.otherMachineTime")}
                </Label>
                <Toggle
                  value={otherMachineTimeMode}
                  onChange={handleOtherMachineTimeModeChange}
                  items={[
                    { value: "model", label: t("settings.defaultProcessStepTimeMode.model") },
                    { value: "fixed", label: t("settings.defaultProcessStepTimeMode.fixed") },
                  ]}
                />
              </Group>
              {otherMachineTimeMode === "fixed" && (
                <TextInput
                  value={cycleTimeFormula ?? ""}
                  onChange={(e) => setCycleTimeFormula(e.target.value)}
                  rightSection={
                    cycleTimeFormula && isPlainNumberString(cycleTimeFormula) ? "min" : undefined
                  }
                />
              )}
            </Stack>

            <NumberInput
              label={t("processStep.machineHourlyPrice")}
              value={machineHourlyPrice}
              onChange={setMachineHourlyPrice}
              min={0}
              decimalScale={2}
              unit="€/h"
            />
          </>
        )}

        {displayPartLevels && (
          <Stack gap="xs">
            <Label helper={undefined} classNames={{ label: inputClasses.label }}>
              {t("settings.defaultProcessStepPartLevels.label")}
            </Label>
            {PART_LEVELS_PRESET_KEYS.map((preset) => (
              <SelectableCard
                key={preset}
                title={t(`settings.defaultProcessStepPartLevels.options.${preset}.title`)}
                description={t(
                  `settings.defaultProcessStepPartLevels.options.${preset}.description`,
                )}
                selected={selectedPartLevelsPreset === preset}
                onSelect={() => setPartLevels(PART_LEVELS_PRESETS[preset])}
              />
            ))}
          </Stack>
        )}

        <Group justify="flex-end">
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSubmit} loading={isPending} disabled={!selectedProcessValue}>
            {mode === "create" ? t("common.add") : t("common.validate")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
